import { RequestPaymentState, ResidentPaymentStatus } from '../types/Payments';
import { RequestInstance } from '../types/OrderStatus';

const PAYMENT_STATUS_SET: ResidentPaymentStatus[] = [
    'pending',
    'processing',
    'succeeded',
    'failed',
    'skipped',
    'canceled',
    'refunded',
    'partially_refunded',
    'action_required',
];

const paymentStatusSetLookup = new Set(PAYMENT_STATUS_SET);

export type PaymentOrderLike = (RequestInstance | Partial<RequestInstance>) & {
    payment_state?: string;
    paymentState?: string;
    payment?: RequestPaymentState;
    Payment?: RequestPaymentState;
    paymentStatus?: string;
    PaymentStatus?: string;
};

export const PAYMENT_STATUS_COLORS: Record<ResidentPaymentStatus, string> = {
    pending: '#C18400',
    processing: '#1264A3',
    succeeded: '#137333',
    failed: '#B42318',
    skipped: '#5F6368',
    canceled: '#7A4B00',
    // Same colours as the payment history list (TRANSACTION_STATUS_COLORS).
    refunded: '#1264A3',
    partially_refunded: '#A16207',
    action_required: '#C18400',
};

export const normalizePaymentStatus = (status?: string | null): ResidentPaymentStatus => {
    const normalized = String(status || '').toLowerCase();

    if (paymentStatusSetLookup.has(normalized as ResidentPaymentStatus)) {
        return normalized as ResidentPaymentStatus;
    }

    if (normalized === 'cancelled') {
        return 'canceled';
    }

    if (normalized === 'payment_action_required' || normalized === 'requires_action') {
        return 'action_required';
    }

    if (!normalized) {
        return 'pending';
    }

    return 'pending';
};

const findPaymentState = (order?: PaymentOrderLike): RequestPaymentState | undefined => {
    if (!order) return undefined;
    if (order.payment && typeof order.payment === 'object') return order.payment as RequestPaymentState;
    if (order.Payment && typeof order.Payment === 'object') return order.Payment as RequestPaymentState;
    return undefined;
};

const resolveRawPaymentStatus = (order?: PaymentOrderLike): string | undefined => {
    if (!order) return undefined;

    const nested = findPaymentState(order);
    if (nested && nested.status) return nested.status;

    return (
        order.paymentStatus
        || order.PaymentStatus
        || order.payment_state
        || order.paymentState
        || undefined
    );
};

type PaymentRequiredLike = {
    defaults?: {
        isPaymentRequired?: boolean;
    } | null;
} | null | undefined;

export const isPaymentRequiredForService = (service?: PaymentRequiredLike): boolean => {
    return Boolean(service?.defaults?.isPaymentRequired);
};

export const resolveOrderPaymentStatus = (
    order: PaymentOrderLike | undefined,
    facilityPaymentsEnabled: boolean,
): ResidentPaymentStatus => {
    const rawStatus = resolveRawPaymentStatus(order);
    if (rawStatus) {
        return normalizePaymentStatus(rawStatus);
    }

    if (!facilityPaymentsEnabled) {
        return 'skipped';
    }

    if (isPaymentRequiredForService(order?.Service)) {
        return 'pending';
    }

    return 'skipped';
};

export const getPaymentStateForOrder = (order?: PaymentOrderLike): RequestPaymentState | undefined => {
    return findPaymentState(order);
};

// True when the order carries a real payment record. An empty object or a skip record (a consent
// payload, or "no payment needed") is not enough to show payment details. Reads the status the same
// way as the badge (resolveOrderPaymentStatus), so the two cannot disagree.
export const hasChargedPaymentState = (order?: PaymentOrderLike): boolean => {
    const status = resolveRawPaymentStatus(order);
    return Boolean(status) && normalizePaymentStatus(status) !== 'skipped';
};

// F16: the tax-inclusive total live stores on payable requests (updated whenever the
// billed amount changes). Falls back to the pre-tax add-on sum for legacy/non-payable ones.
export const getOrderDisplayTotal = (order: PaymentOrderLike | undefined, computedTotal: number): number => {
    const state = findPaymentState(order);
    const displayedCents = Number(state && state.amountDisplayedCents);
    if (Number.isFinite(displayedCents) && displayedCents > 0) {
        return displayedCents / 100;
    }
    return Number(computedTotal) || 0;
};

const PAYMENT_STATUS_LABELS: Record<ResidentPaymentStatus, string> = {
    pending: 'Pending',
    processing: 'Processing',
    succeeded: 'Paid',
    failed: 'Payment Failed',
    skipped: 'Not Charged',
    canceled: 'Canceled',
    refunded: 'Refunded',
    partially_refunded: 'Partially Refunded',
    action_required: 'Action Required',
};

export const formatPaymentStatusLabel = (status: ResidentPaymentStatus): string => PAYMENT_STATUS_LABELS[status];

// Shown on hover for statuses whose short label needs a next step.
export const PAYMENT_STATUS_HINTS: Partial<Record<ResidentPaymentStatus, string>> = {
    action_required: 'Your bank needs to confirm this payment. Please contact the front desk.',
};

// Plain-language reason for an order's payment, or null when there is nothing the resident
// needs to know. Live also stores internal codes (skips, deferrals, refund notes) here.
export const getOrderPaymentReasonLabel = (paymentState?: RequestPaymentState): string | null => {
    const reason = String((paymentState && paymentState.reason) || '');
    if (!reason) return null;
    if (reason === 'payment_method_required') return 'No card on file';
    if (reason === 'consent_amount_exceeded') return 'Price changed, staff will follow up';
    const status = normalizePaymentStatus(paymentState && paymentState.status);
    if (status === 'failed' || status === 'action_required') return 'Payment issue, contact the front desk';
    return null;
};
